import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Link2Off, Search } from "lucide-react";
import api, { errorText } from "../../../lib/api";
import { useDash, money, clock } from "../../../lib/dash";
import { useDebounced } from "../../../hooks/useDebounced";
import { TileState, Stat, Chip } from "../Tile";

export const SOURCE_TONE = { "own duck": "green", "linked by admin": "blue" };
export const orderNum = (id) => String(id).split("/").pop();
export const revenueText = (r) => Object.entries(r || {}).map(([c, v]) => money(v, c)).join(" + ") || money(0);
export const ducksText = (o) => o.ducks.map((k) => `${k.name || "duck"} × ${k.quantity}`).join(", ");

const SmallBtn = ({ testid, onClick, disabled, icon: Icon, children }) => (
  <button type="button" data-testid={testid} onClick={onClick} disabled={disabled}
    className="flex shrink-0 items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40">
    <Icon className="h-3 w-3" /> {children}
  </button>
);

// Admin only: find any order by number or customer name (the customer's name is never shown to the partner).
const OrderSearch = ({ linked, canAct, onLink }) => {
  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim(), 300);
  const [hits, setHits] = useState(null);
  useEffect(() => {
    if (!dq) { setHits(null); return undefined; }
    let live = true;
    api.get(`/accounts/orders/search?q=${encodeURIComponent(dq)}`)
      .then(({ data }) => live && setHits(data.orders)).catch(() => live && setHits([]));
    return () => { live = false; };
  }, [dq]);
  return (
    <div className="space-y-1">
      <label className="relative block">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="partner-order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find an order: number or customer name"
          className="h-8 w-full rounded-lg border border-input bg-background pl-8 pr-2 text-[12.5px]" />
      </label>
      {hits && (hits.length ? (
        <ul data-testid="partner-order-hits" className="rounded-lg border border-border px-2">
          {hits.map((o) => (
            <li key={o.order_id} className="flex items-center justify-between gap-2 border-b border-border/60 py-1 text-[12px] last:border-0">
              <span className="min-w-0 truncate"><b>{o.order_name}</b> · {o.customer_name || "no name"} · {o.ducks} duck{o.ducks === 1 ? "" : "s"} · {clock(o.created_at)}</span>
              {linked.has(o.order_id) ? <Chip>linked</Chip> : (
                <SmallBtn testid={`partner-link-${orderNum(o.order_id)}`} icon={Link2} disabled={!canAct} onClick={() => onLink(o.order_id)}>Link</SmallBtn>
              )}
            </li>
          ))}
        </ul>
      ) : <p data-testid="partner-order-no-hits" className="text-[12px] text-muted-foreground">No order matches.</p>)}
    </div>
  );
};

const OrderRow = ({ o, canManage, canAct, onUnlink }) => (
  <li data-testid={`partner-order-${orderNum(o.order_id)}`} className="flex items-center gap-2 border-b border-border/60 py-1.5 text-[12.5px] last:border-0">
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-1.5"><b>{o.order_name}</b> <Chip tone={SOURCE_TONE[o.source]}>{o.source}</Chip> <span className="text-[11px] text-muted-foreground">{o.status}</span></span>
      <span className="block truncate text-[11px] text-muted-foreground">{clock(o.created_at)} · {ducksText(o)}</span>
    </span>
    <span className="shrink-0 font-semibold tabular-nums">{money(o.amount, o.currency)}</span>
    {canManage && <SmallBtn testid={`partner-unlink-${orderNum(o.order_id)}`} icon={Link2Off} disabled={!canAct} onClick={() => onUnlink(o.order_id)}>Unlink</SmallBtn>}
  </li>
);

const Linked = ({ data, canManage, canAct, onAct }) => (
  <>
    <div className="grid grid-cols-3 gap-2">
      <Stat testid="partner-admin-orders" label="Orders" value={data.totals.orders} sub={data.totals.cancelled ? `${data.totals.cancelled} cancelled` : "all time"} />
      <Stat testid="partner-admin-ducks" label="Ducks" value={data.totals.ducks} />
      <Stat testid="partner-admin-sales" label="Sales" value={revenueText(data.totals.revenue)} />
    </div>
    {data.orders.length ? (
      <ul data-testid="partner-order-list">
        {data.orders.map((o) => <OrderRow key={o.order_id} o={o} canManage={canManage} canAct={canAct} onUnlink={(id) => onAct(id, "unlink")} />)}
      </ul>
    ) : <p data-testid="partner-orders-empty" className="py-1 text-[12px] text-muted-foreground">No orders yet: none with their own ducks, none linked by hand.</p>}
    {data.unlinked.length > 0 && (
      <ul data-testid="partner-unlinked" className="rounded-lg bg-muted/50 px-2">
        {data.unlinked.map((u) => (
          <li key={u.order_id} className="flex items-center justify-between gap-2 py-1 text-[12px]">
            <span className="min-w-0 truncate">Unlinked by admin: order {orderNum(u.order_id)} · {u.reason}</span>
            {canManage && <SmallBtn testid={`partner-relink-${orderNum(u.order_id)}`} icon={Link2} disabled={!canAct} onClick={() => onAct(u.order_id, "link")}>Link again</SmallBtn>}
          </li>
        ))}
      </ul>
    )}
  </>
);

// A partner's orders as the admin sees them: own-duck orders come by themselves; any other order is linked by hand.
export const PartnerOrders = ({ id, canManage }) => {
  const s = useDash(`/accounts/${id}/partner-orders`, { poll: false });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (order_id, action) => {
    setBusy(true);
    try {
      await api.post(`/accounts/${id}/partner-orders`, { order_id, action, reason: reason.trim() });
      toast.success(`Order ${action === "link" ? "linked" : "unlinked"} · written to the admin audit`);
      s.reload();
    } catch (e) {
      toast.error(errorText(e, "Could not change the link."));
    } finally {
      setBusy(false);
    }
  };
  const canAct = canManage && reason.trim().length >= 3 && !busy;
  const linked = new Set((s.data?.orders || []).map((o) => o.order_id));
  return (
    <div data-testid="partner-orders" className="space-y-2.5">
      {canManage && (
        <div className="space-y-1.5 rounded-xl border border-border p-2">
          <input data-testid="partner-link-reason" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for a link or unlink (required, admin audit)" className="h-8 w-full rounded-lg border border-input bg-background px-2 text-[12.5px]" />
          <OrderSearch linked={linked} canAct={canAct} onLink={(oid) => act(oid, "link")} />
        </div>
      )}
      <TileState state={s}>{s.data && <Linked data={s.data} canManage={canManage} canAct={canAct} onAct={act} />}</TileState>
    </div>
  );
};
